import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  HeartPulse,
  UserX,
  XCircle,
  ArrowRight,
} from 'lucide-react';
import DashboardCard from './DashboardCard';
import type { LeaveAbsenceItem } from './types';

const leaveIconMap = {
  calendar: CalendarDays,
  heart: HeartPulse,
  'user-x': UserX,
  'x-circle': XCircle,
};

interface LeaveAndPayrollSectionProps {
  leaveAbsence: LeaveAbsenceItem[];
  payrollReadiness: {
    percent: number;
    approved: number;
    pending: number;
    rejected: number;
    total: number;
  };
  /** @deprecated Prefer leaveLoading / payrollLoading for independent settle. */
  loading?: boolean;
  leaveLoading?: boolean;
  payrollLoading?: boolean;
}

const PayrollRing: React.FC<{ percent: number }> = ({ percent }) => {
  const size = 88;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (percent / 100) * circumference;

  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#E5E7EB" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#2563EB"
          strokeWidth={stroke}
          strokeDasharray={`${progress} ${circumference - progress}`}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-gray-900">{percent}%</span>
      </div>
    </div>
  );
};

const LeaveAndPayrollSection: React.FC<LeaveAndPayrollSectionProps> = ({
  leaveAbsence,
  payrollReadiness,
  loading,
  leaveLoading,
  payrollLoading,
}) => {
  const navigate = useNavigate();
  const showLeaveLoading = leaveLoading ?? loading ?? false;
  const showPayrollLoading = payrollLoading ?? loading ?? false;

  return (
    <div className="flex flex-col gap-4 h-full">
      {showLeaveLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 h-36 animate-pulse" />
      ) : (
        <DashboardCard title="Leave & Absence Overview">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 pb-5">
            {leaveAbsence.map((item) => {
              const Icon = leaveIconMap[item.icon];
              return (
                <div key={item.label} className="text-center">
                  <div className={`inline-flex p-2.5 rounded-lg ${item.bg} mb-2`}>
                    <Icon className={`h-5 w-5 ${item.color}`} />
                  </div>
                  <p className="text-xl font-bold text-gray-900">{item.count}</p>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-tight">{item.label}</p>
                </div>
              );
            })}
          </div>
        </DashboardCard>
      )}

      {showPayrollLoading ? (
        <div className="bg-white rounded-lg border border-gray-200 h-44 animate-pulse flex-1" />
      ) : (
        <DashboardCard title="Payroll Readiness" className="flex-1">
          <div className="px-5 pb-4">
            <div className="flex items-stretch gap-0">
              <div className="flex items-center justify-center pr-5 border-r border-gray-100">
                <PayrollRing percent={payrollReadiness.percent} />
              </div>

              <div className="flex-1 pl-5 flex flex-col justify-center min-w-0">
                <div className="pb-3 mb-3 border-b border-gray-100">
                  <p className="text-base font-semibold text-gray-900">
                    {payrollReadiness.percent}% Timecards Approved
                  </p>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {payrollReadiness.approved} of {payrollReadiness.total} timecards
                  </p>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Approved
                    </span>
                    <span className="font-semibold text-gray-900">{payrollReadiness.approved}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-2 h-2 rounded-full bg-orange-400" />
                      Pending
                    </span>
                    <span className="font-semibold text-gray-900">{payrollReadiness.pending}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className="w-2 h-2 rounded-full bg-red-500" />
                      Rejected
                    </span>
                    <span className="font-semibold text-gray-900">{payrollReadiness.rejected}</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => navigate('/timecards')}
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              View Timecards
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </DashboardCard>
      )}
    </div>
  );
};

export default LeaveAndPayrollSection;
